import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import ConsentAwareEmbed from "./ConsentAwareEmbed";

function extractVideoId(url) {
  const match = url.match(/embed\/([^?&/]+)/);
  return match ? match[1] : null;
}

function buildEmbedUrl(url, autoplay = false) {
  const videoId = extractVideoId(url);
  if (!videoId) return url;

  // Extraer parámetros originales excepto si= (token de sesión que expira)
  const originalParams = new URLSearchParams(
    url.includes("?") ? url.split("?")[1] : ""
  );
  originalParams.delete("si");

  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    enablejsapi: "0",
    ...(autoplay ? { autoplay: "1" } : {}),
  });

  // Copiar params originales sin si=
  for (const [key, val] of originalParams.entries()) {
    params.set(key, val);
  }

  // youtube-nocookie.com evita cookies de seguimiento y restricciones de origen
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}

const YoutubePlayer = ({ url, title = "Video" }) => {
  const [playing, setPlaying] = useState(false);
  const videoId = extractVideoId(url);
  const embedUrl = buildEmbedUrl(url, true);
  const thumbnailUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : null;

  const handlePlay = useCallback(() => setPlaying(true), []);

  if (!videoId) return null;

  return (
    <div className="absolute inset-0 w-full h-full bg-black">
      <ConsentAwareEmbed
        title="Video de YouTube"
        description="YouTube es un servicio de terceros. Actívelo para reproducir el video."
        className="absolute inset-0 rounded-none h-full border-0"
        minHeight="100%"
      >
      {playing ? (
        <iframe
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 w-full h-full border-0"
          loading="lazy"
        />
      ) : (
        <motion.button
          onClick={handlePlay}
          className="absolute inset-0 w-full h-full group cursor-pointer focus:outline-none"
          aria-label={`Reproducir: ${title}`}
          whileHover="hover"
        >
          {/* Thumbnail */}
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}

          {/* Overlay oscuro */}
          <motion.div
            className="absolute inset-0 bg-black/30"
            variants={{ hover: { backgroundColor: "rgba(0,0,0,0.15)" } }}
            transition={{ duration: 0.2 }}
          />

          {/* Botón play */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            variants={{ hover: { scale: 1.1 } }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
          >
            <div className="w-20 h-20 rounded-full bg-white/95 shadow-2xl flex items-center justify-center">
              {/* Triángulo play */}
              <svg
                width="28" height="28" viewBox="0 0 24 24"
                fill="#658b10" className="ml-1"
              >
                <polygon points="5,3 19,12 5,21" />
              </svg>
            </div>
          </motion.div>

          {/* Etiqueta inferior */}
          <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/70 to-transparent">
            <p className="text-white text-sm font-semibold truncate">{title}</p>
            <p className="text-white/60 text-xs mt-0.5">Ver en reproductor</p>
          </div>
        </motion.button>
      )}
      </ConsentAwareEmbed>
    </div>
  );
};

export default YoutubePlayer;
